import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import ReactCountryFlag from "react-country-flag";

function Logs() {
  // Initially, no server is selected.
  const [server, setServer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [logsType, setLogsType] = useState("");
  const [logs, setLogs] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  // An empty filterDate means current logs (no date filter)
  const [filterDate, setFilterDate] = useState("");
  // New state to control whether available dates are loading.
  const [loadingDates, setLoadingDates] = useState(false);

  // Whenever the server selection changes, fetch available dates from the target server.
  useEffect(() => {
    async function fetchDates() {
      if (!server) {
        setAvailableDates([]);
        return;
      }
      setLoadingDates(true);
      try {
        const response = await axios.get(
          `/api/availableLogDates?server=${server}`,
          { withCredentials: true }
        );
        setAvailableDates(response.data.dates);
      } catch (error) {
        console.error("Error fetching available log dates", error);
      } finally {
        setLoadingDates(false);
      }
    }
    fetchDates();
  }, [server]);

  const handleFindLogs = async () => {
    // Require that a server is selected before fetching logs.
    if (!server) {
      setErrorMessage("Please select a server.");
      return;
    }
    if (!searchTerm || searchTerm.trim().length < 2) {
      setErrorMessage(
        "Please enter a precise search term (at least 2 characters)."
      );
      return;
    }
    if (!logsType) {
      setErrorMessage("Please select a logs type.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSearched(false);

    // Create a cancel token for axios with a 20-second timeout.
    const source = axios.CancelToken.source();
    const timeoutId = setTimeout(() => {
      source.cancel("Request timed out");
    }, 20000);

    try {
      const response = await axios.post(
        "/api/fetchLogs",
        { server, searchTerm, logsType, filterDate },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          cancelToken: source.token,
        }
      );
      clearTimeout(timeoutId);
      setLogs(response.data.logs);
      setSearched(true);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error("Axios request canceled:", error.message);
      } else {
        if (error.response) {
          if (error.response.status === 403) {
            setErrorMessage("You do not have permission to fetch logs.");
          } else if (error.response.status === 404) {
            setErrorMessage("No logs were found for the selected filters.");
          } else if (error.response.status === 400) {
            setErrorMessage(
              "The output is abnormally large. Please adjust your search filters."
            );
          } else {
            setErrorMessage(
              error.response.data.error ||
                "Failed to fetch logs. Please try again."
            );
          }
        } else {
          setErrorMessage("Failed to fetch logs. Please try again.");
        }
        console.error("Axios error:", error);
      }
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setErrorMessage("");
  };

  return (
    <Container style={{ marginTop: 20 }}>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="flex-start"
      >
        {/* Server Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="server-select-label">Server</InputLabel>
            <Select
              labelId="server-select-label"
              id="server-select"
              value={server}
              label="Server"
              onChange={(e) => setServer(e.target.value)}
            >
              <MenuItem value="">
                <em>Select Server</em>
              </MenuItem>
              <MenuItem value="NA">
                <ReactCountryFlag
                  countryCode="US"
                  svg
                  style={{
                    width: "1.5em",
                    height: "1.5em",
                    marginRight: "8px",
                  }}
                  title="United States"
                />
                North America (NA)
              </MenuItem>
              <MenuItem value="EU">
                <ReactCountryFlag
                  countryCode="EU"
                  svg
                  style={{
                    width: "1.5em",
                    height: "1.5em",
                    marginRight: "8px",
                  }}
                  title="Europe"
                />
                Europe (EU)
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Logs Type Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="logs-type-select-label">Logs Type</InputLabel>
            <Select
              labelId="logs-type-select-label"
              id="logs-type-select"
              value={logsType}
              label="Logs Type"
              onChange={(e) => setLogsType(e.target.value)}
            >
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Chat">Chat</MenuItem>
              <MenuItem value="Item">Item</MenuItem>
              <MenuItem value="Map">Map</MenuItem>
              <MenuItem value="Server Debug">Server Debug</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Date Filter Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={!server || loadingDates}
          >
            <InputLabel id="date-select-label">Date Filter</InputLabel>
            <Select
              labelId="date-select-label"
              id="date-select"
              value={filterDate}
              label="Date Filter"
              onChange={(e) => setFilterDate(e.target.value)}
            >
              <MenuItem value="">
                <em>Current Logs</em>
              </MenuItem>
              {loadingDates ? (
                <MenuItem value="" disabled>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  Loading dates...
                </MenuItem>
              ) : (
                availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Instructional Message */}
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Note: Please enter a precise search term (at least 2 characters) and
            select a logs type to narrow down results and avoid large output and
            long processing times.
          </Typography>
        </Grid>

        {/* Search Term Input & Find Logs Button */}
        <Grid item xs={12} sm={9}>
          <TextField
            label="Search Term"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFindLogs}
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Find Logs"}
          </Button>
        </Grid>
      </Grid>

      {/* Logs Display */}
      <div style={{ marginTop: 20 }}>
        {searched && logs.length === 0 && !loading ? (
          <Typography variant="body1">
            No logs found for the selected filters.
          </Typography>
        ) : (
          logs.map((log, index) => (
            <Typography key={index} variant="body1">
              {log}
            </Typography>
          ))
        )}
      </div>

      {/* Error Notification Snackbar */}
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Logs;
